import { cn } from "@/lib/utils/cn";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Optional explanatory line under the label; the control grows to fit. */
  hint?: string;
}

export interface SegmentedControlProps<T extends string> {
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Accessible name for the group. */
  label: string;
  className?: string;
}

/**
 * SegmentedControl — a 2..3 way switch between sibling views.
 *
 * **Why this is a new primitive and not `FilterChipGroup` (P7).** Profile spec
 * §7.2 says to reuse a primitive if one exists; the closest is P7, and it is
 * the wrong component. P7 is a *filter* over one list — it multiplies what is
 * already on screen — and its accent taxonomy (`you` / `partner` / `shared` /
 * `paper-deep` / `ink-2` / `gold`) has no `ink-1`. This control switches
 * between two different datasets. Overloading P7 would have meant either
 * adding an accent that is a selection state rather than a category, or
 * restyling it — both of which change a documented public API.
 *
 * **The settled selection is a shape, not a solid fill** (W56.0). It used to
 * be `bg-ink-1 text-paper` — a near-black fill measured at 16.31:1, which on
 * a screen with a primary button made the *switch* louder than the action.
 * It now uses the same language as `FilterChipGroup`: a raised surface fill,
 * a stronger hairline border, and a 6px dot. The dot's slot is occupied in
 * both states — only its opacity changes — so the segment's width never
 * jumps when the selection moves.
 *
 * **Hit targets are 44×44** via the real `.tap-target` class in
 * `styles/globals.css`. Note that `tap-target-none`, used by the calendar
 * editor, is **not** defined there — it is a dead class that silently does
 * nothing. Do not copy it.
 *
 * State is carried by `data-selected` on each button, never an inline style
 * (`component-api.md` §10).
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={label}
      data-component="SegmentedControl"
      className={cn(
        "flex gap-s-1 rounded-pill bg-paper-deep p-s-1 border border-hairline",
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            data-selected={selected || undefined}
            data-testid={`segmented-${option.value}`}
            onClick={() => onChange(option.value)}
            className={cn(
              // `.tap-target` is the semantic marker; the explicit `min-h-11 min-w-11`
              // is what `expectTouchTarget` can actually verify in jsdom, which has
              // no layout engine. Both, deliberately — see tests/viewport.ts.
              "tap-target press min-h-11 min-w-11 flex-1 rounded-pill px-s-4 text-body-sm font-semibold",
              "transition-colors duration-[--dur-quick] ease-[--ease-quiet]",
              // Both states reserve the same 1px border, so switching does not
              // resize the segment and the label does not shift.
              "border",
              selected
                ? "bg-paper-elevated border-hairline-strong text-ink-1"
                : "border-transparent text-ink-2 hover:bg-black/5",
            )}
          >
            {/* The dot's slot is occupied in BOTH states — only its opacity
                changes. A dot that appeared with the selection would resize the
                segment and shift the label under the user's thumb. */}
            <span className="inline-flex items-center gap-1.5 leading-tight">
              <span
                aria-hidden
                data-component="SegmentedControlDot"
                className={cn(
                  "h-1.5 w-1.5 shrink-0 rounded-full bg-ink-1",
                  selected ? "opacity-100" : "opacity-0",
                )}
              />
              <span>{option.label}</span>
            </span>
            {option.hint ? (
              <span
                className={cn(
                  "mt-0.5 block text-meta font-normal leading-tight",
                  selected ? "text-ink-2" : "text-ink-3",
                )}
              >
                {option.hint}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
